<template>
  <v-navigation-drawer
    v-if="showNavigationBar"
    ref="navDrawer"
    v-model="drawer"
    :mini-variant="navState"
    app
    mini-variant-width="63"
    permanent
    :touchless="true"
    class="main-nav"
    :width="drawerWidth"
  >
    <v-row
      class="flex-nowrap"
      :width="drawerWidth"
    >
      <v-col class="ma-0 pb-0">
        <div class="logo-container">
          <v-btn
            aria-label="Navigation"
            icon
            @click.stop="setNavState"
          >
            <v-icon>{{ drawerIcon }}</v-icon>
          </v-btn>

          <router-link to="/">
            <img
              src="../../assets/images/oao-iadops-logo-with-blue-divider.png"
              class="d-flex pl-4"
              aria-label="OAO logo"
            >
          </router-link>
        </div>

        <v-divider />

        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <a
              href="javascript:;"
              :class="`logged-in-user-info cursor-pointer ${textColor}--text ${navState ? 'ml-2 py-2' : 'ml-4 py-1'}`"
              v-on="on"
            >
              <v-avatar>
                <img
                  v-if="user.photoUrl"
                  :src="user.photoURL"
                  alt="Avatar"
                >

                <v-icon v-else>account_circle</v-icon>
              </v-avatar>

              <div class="user-details">
                <div :class="`user-name subtitle-2 font-weight-bold ${titleColor}--text`">
                  {{ user.personName }}
                </div>

                <div
                  class="user-email body-2 font-weight-medium"
                >
                  {{ user.email }}
                </div>

                <div
                  v-if="inAliasMode"
                  class="user-email caption"
                >
                  <span
                    class="user-email"
                  >
                    as
                  </span>
                  <span class="user-email">
                    {{ aliasEmail }}
                  </span>
                </div>

                <div
                  v-if="inDemoMode"
                  class="user-email font-weight-medium primary--text caption"
                >
                  Demo Mode On
                </div>
              </div>
            </a>
          </template>

          <v-list>
            <v-list-item
              v-if="hasPermissionForSettings"
              class="caption"
              @click="displaySettingsModal"
            >
              <v-list-item-title class="coolgray--text">
                <v-icon
                  class="pr-2 coolgray--text"
                  small
                >
                  format_paint
                </v-icon>
                <span>Settings</span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="signOut">
              <v-list-item-title class="toasted--text">
                <v-icon
                  class="pr-2 toasted--text"
                  small
                >
                  power_settings_new
                </v-icon>
                <span>Sign Out</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-divider />

        <v-list :class="`py-0 menu-items ${navState ? 'pl-0' : 'pl-1'}`">
          <div
            v-for="(nav, navIndex) in navigation"
            :key="navIndex"
            :style="`order: ${nav.navigationIndex};`"
          >
            <v-divider
              v-if="nav.divider === 'pre'"
            />

            <v-list-item
              :class="`menu-item ${navState ? 'pl-4' : 'pl-5'}`"
              :to="nav.link"
              active-class="router-active-link"
            >
              <v-list-item-action class="ma-0">
                <img :src="nav.image">
              </v-list-item-action>

              <v-list-item-content :class="`${navState ? 'ml-1' : 'ml-5'} py-0 font-weight-medium`">
                <v-list-item-title>{{ nav.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <div class="sub-items-container">
              <router-link
                v-for="(subNav, subNavIndex) in nav.subItems"
                :key="subNavIndex"
                class="sub-item-link font"
                :to="nav.href"
              >
                {{ subNav.name }}
              </router-link>

              <div
                v-if="nav.customAnalytics"
                class="nav-queries pb-4 mx-7 my-4"
              >
                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 coolgray--text font-weight-bold"
                    @click="editCustomAnalyticsList"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                    <span class="pl-6">Active</span>
                  </v-btn>
                </v-row>

                <v-row
                  v-for="(customAnalytic, customAnalyticIndex) in customAnalyticOptions"
                  :key="customAnalyticIndex"
                  class="flex-nowrap text-truncate"
                >
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="editCustomAnalytic(customAnalytic)"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                  </v-btn>

                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="navigateToCustomAnalytic(customAnalytic)"
                  >
                    <span>{{ customAnalytic.label }}</span>
                  </v-btn>
                </v-row>

                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 primary--text"
                    @click="addCustomAnalytic"
                  >
                    <v-icon x-small>
                      add
                    </v-icon>
                    <span class="pl-6">Add to List</span>
                  </v-btn>
                </v-row>
              </div>

              <div
                v-if="nav.networks"
                class="nav-queries pb-4 mx-7 my-4"
              >
                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 coolgray--text font-weight-bold"
                    @click="editNetworkList"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                    <span class="pl-6">Networks</span>
                  </v-btn>
                </v-row>

                <v-row
                  v-for="(favoriteNetwork, favoriteNetworkIndex) in networks"
                  :key="favoriteNetworkIndex"
                  class="flex-nowrap text-truncate"
                >
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="editNetwork(favoriteNetworkIndex)"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                  </v-btn>

                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="navigateToNetworkDashboard(favoriteNetwork)"
                  >
                    <span>{{ favoriteNetwork.label }}</span>
                  </v-btn>
                </v-row>

                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 primary--text"
                    :disabled="!isInternalUser"
                    @click="addNetwork"
                  >
                    <v-icon x-small>
                      add
                    </v-icon>
                    <span class="pl-6">Add Network</span>
                  </v-btn>
                </v-row>
              </div>

              <div
                v-if="nav.times"
                class="nav-queries pb-4 mx-7 my-4"
              >
                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 coolgray--text font-weight-bold"
                    @click="editTimeDateRangeList"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                    <span class="pl-6">Date Ranges</span>
                  </v-btn>
                </v-row>

                <v-row
                  v-for="(dateRange, dateRangeIndex) in timesheets"
                  :key="dateRangeIndex"
                  class="flex-nowrap text-truncate"
                >
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="editTimeDateRange(dateRangeIndex)"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                  </v-btn>

                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="navigateToTimeDateRange(dateRange)"
                  >
                    <span>{{ dateRange.text }}</span>
                  </v-btn>
                </v-row>

                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 primary--text"
                    @click="addTimeDateRange"
                  >
                    <v-icon x-small>
                      add
                    </v-icon>
                    <span class="pl-6">Add Range</span>
                  </v-btn>
                </v-row>
              </div>

              <div
                v-if="nav.queries"
                class="nav-queries pb-4 mx-7 my-4"
              >
                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 coolgray--text font-weight-bold"
                    @click="editQueriesList(nav)"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                    <span class="pl-6">Queries</span>
                  </v-btn>
                </v-row>

                <v-row
                  v-for="(query, queryIndex) in nav.queries"
                  :key="queryIndex"
                >
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="editQuery(queryIndex, nav)"
                  >
                    <v-icon x-small>
                      edit
                    </v-icon>
                  </v-btn>

                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3"
                    @click="navigateToQuery(query)"
                  >
                    <span>{{ query.reportLabel }}</span>
                  </v-btn>
                </v-row>

                <v-row>
                  <v-btn
                    text
                    x-small
                    class="d-flex text-smaller pa-3 primary--text"
                    :disabled="nav.baseReportName === 'time-sheet'"
                    @click="addQuery(nav)"
                  >
                    <v-icon x-small>
                      add
                    </v-icon>
                    <span class="pl-6">Create Query</span>
                  </v-btn>
                </v-row>
              </div>
            </div>

            <v-divider
              v-if="nav.divider === 'post'"
            />
          </div>

          <div
            :style="`order: ${navigation.length + 1}`"
          >
            <v-divider />

            <v-list-item
              :class="`menu-item ${navState ? 'pl-4' : 'pl-5'}`"
              :to="docRoute.link"
              active-class="router-active-link"
            >
              <v-list-item-action class="mr-0">
                <img :src="docRoute.image">
              </v-list-item-action>

              <v-list-item-content :class="navState ? 'ml-1' : 'ml-5'">
                <v-list-item-title class="font-weight-medium">
                  {{ docRoute.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider />

            <v-list-item
              v-if="!navState"
              :class="`menu-item ${navState ? 'pl-4' : 'pl-5'}`"
            >
              <v-list-item-content class="ml-1">
                <div class="links-container">
                  <div
                    class="copywrite"
                    to="/docs/privacy-policy"
                  >
                    &copy; {{ new Date().getFullYear() }}, OAO
                  </div>
                  <div
                    class="caption"
                  >
                    {{ version }}
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list>
      </v-col>

      <v-col
        id="drawer-resize"
        :class="`col-auto ma-0${isDraggingNavigation ? ' is-resizing-navigation': ''}`"
        @mousedown="resizeDrawerStart"
      />
    </v-row>
  </v-navigation-drawer>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const {
  mapGetters: mapAuthGetters,
  mapActions: mapAuthActions,
  mapState: mapAuthState,
} = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters, mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Navigation',
  components: {
  },
  data() {
    return {
      drawer: true,
    }
  },
  computed: {
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      user: (state) => state.user,
    }),
    ...mapAuthGetters([
      'aliasEmail',
      'defaultNetwork',
      'inAliasMode',
      'inDemoMode',
      'isActiveUser',
      'isInternalUser',
      'permissions',
    ]),
    ...mapLayoutGetters([
      'textColor',
      'titleColor',
    ]),
    ...mapToolsGetters([
      'currentPageName',
      'customAnalyticOptions',
      'networks',
      'preparedReports',
      'selectedSearch',
      'timesheets',
    ]),
    ...mapToolsState({
      $navigation: (state) => state.navigation,
      baseReportName: (state) => state.baseReportName,
    }),
    ...mapLayoutState({
      drawerDefaults: (state) => state.drawerDefaults,
      drawerDragStartX: (state) => state.drawerDragStartX,
      drawerDragStartWidth: (state) => state.drawerDragStartWidth,
      drawerWidth: (state) => state.drawerWidth,

      formCustomAnalytic: (state) => state.formCustomAnalytic,
      formNetwork: (state) => state.formNetwork,
      formQuery: (state) => state.formQuery,

      icons: (state) => state.icons,

      $isLayoutFullScreen: (state) => state.isLayoutFullScreen,

      isModalVisibleCustomAnalytic: (state) => state.isModalVisibleCustomAnalytic,
      isModalVisibleNetwork: (state) => state.isModalVisibleNetwork,
      isModalVisibleQuery: (state) => state.isModalVisibleQuery,
      isModalVisibleSettings: (state) => state.isModalVisibleSettings,

      isNavCollapsed: (state) => state.isNavCollapsed,
      isResizingNavigation: (state) => state.isResizingNavigation,

      version: (state) => state.version,
    }),
    navigation() {
      const { currentPageName, defaultNetwork } = this
      const { params, query } = this.$route
      const { routeName: currentRouteName } = params
      const { associationId: associationIdOnQuery, subId: subIdOnQuery } = query

      const excludeRoutes = ['home', 'dashboard', 'time-sheet']
      const onExcludedRoute = excludeRoutes.indexOf(currentPageName) !== -1
      const onInternalRoute = currentRouteName === 'internal'

      const getPublisherTeamSubId = () => (subIdOnQuery === 'all-secondary' ? '-1' : subIdOnQuery || '-1')
      const getInternalSubIdAllSecondary = (isInternalRoute) => {
        const isProfileQuery = () => onInternalRoute && subIdOnQuery !== 'all-secondary'
        const isClientQuery = () => subIdOnQuery === '-1' && associationIdOnQuery !== 'all-associations'

        return isInternalRoute && (!subIdOnQuery || subIdOnQuery === 'all-secondary' || isClientQuery() || isProfileQuery())
      }

      return this.$navigation.map((navigation) => {
        const { baseReportName, routeName } = navigation
        const navKey = baseReportName || routeName

        if (excludeRoutes.indexOf(navKey) === -1) {
          const endpointKey = baseReportName ? `${routeName}/${baseReportName}` : routeName
          const isInternalRoute = routeName === 'internal'
          const getDefaultNetworkIdsForExcludedRoute = () => onExcludedRoute && !associationIdOnQuery && defaultNetwork
          const getDefaultNetworkIdsForInternalRoute = () => onInternalRoute && associationIdOnQuery === 'all-associations' && defaultNetwork

          const defaultNetworkIds = !isInternalRoute && (getDefaultNetworkIdsForExcludedRoute() || getDefaultNetworkIdsForInternalRoute())
          const getNetworkAssociationId = () => {
            const isNetworkReportRoute = () => ['delivery', 'programmatic'].indexOf(routeName) !== -1
            const getAssociationId = () => (associationIdOnQuery === 'all-associations' && !isNetworkReportRoute() ? defaultNetwork.associationId : associationIdOnQuery)
            return !isInternalRoute && getAssociationId()
          }
          const getInternalAssociationId = () => isInternalRoute && 'all-associations'
          const getQueryAssociationId = () => getInternalAssociationId() || getNetworkAssociationId()
          const getQuerySubId = () => (getInternalSubIdAllSecondary(isInternalRoute) ? 'all-secondary' : getPublisherTeamSubId())
          const hasAssociationIdOnQuery = (aId) => aId && (aId !== 'all-associations' || isInternalRoute)

          const assocId = defaultNetworkIds ? defaultNetworkIds.associationId : getQueryAssociationId()
          const secondaryId = defaultNetworkIds ? defaultNetworkIds.subId : getQuerySubId()

          const reportPath = baseReportName === 'time-rollup' ? 'month-to-date' : 'all-data'
          const action = defaultNetworkIds || hasAssociationIdOnQuery(assocId) ? 'read' : 'edit'

          navigation.link = `/${endpointKey}/?action=${action}&associationId=${assocId}&reportPath=${reportPath}&subId=${secondaryId}`
        }

        return navigation
      })
    },
    showNavigationBar() {
      return !this.$isLayoutFullScreen
    },
    newDrawerDragStartWidth: {
      get() {
        return this.drawerDragStartWidth
      },
      set(value) {
        this.$store.commit('layout/drawerDragStartWidth', value)
      },
    },
    newDrawerDragStartX: {
      get() {
        return this.drawerDragStartX
      },
      set(value) {
        this.$store.commit('layout/drawerDragStartX', value)
      },
    },
    newDrawerWidth: {
      get() {
        return this.drawerWidth
      },
      set(value) {
        this.$store.commit('layout/drawerWidth', value)
      },
    },

    formDataForCustomAnalytic: {
      get() {
        return this.formCustomAnalytic
      },
      set(value) {
        this.$store.commit('layout/formCustomAnalytic', value, { root: true })
      },
    },
    formDataForNetwork: {
      get() {
        return this.formNetwork
      },
      set(value) {
        this.$store.commit('layout/formNetwork', value, { root: true })
      },
    },
    formDataForReport: {
      get() {
        return this.formQuery[this.formName]
      },
      set(payload) {
        this.$store.commit('layout/formQuery', payload, { root: true })
      },
    },

    entryCustomAnalytic: {
      get() {
        return this.formDataForCustomAnalytic.entry
      },
      set(entry) {
        const { action } = this.formDataForCustomAnalytic
        this.$store.commit('layout/formCustomAnalytic', { action, entry }, { root: true })
      },
    },
    entryNetwork: {
      get() {
        return this.formDataForNetwork.entry
      },
      set(entry) {
        const { action } = this.formDataForNetwork
        this.$store.commit('layout/formNetwork', { action, entry }, { root: true })
      },
    },

    showModalCustomAnalytic: {
      get() {
        return this.isModalVisibleCustomAnalytic
      },
      set(value) {
        this.$store.commit('layout/isModalVisibleCustomAnalytic', value, { root: true })
      },
    },
    showModalNetwork: {
      get() {
        return this.isModalVisibleNetwork
      },
      set(value) {
        this.$store.commit('layout/isModalVisibleNetwork', value, { root: true })
      },
    },
    showModalQuery: {
      get() {
        return this.isModalVisibleQuery
      },
      set(value) {
        this.$store.commit('layout/isModalVisibleQuery', value, { root: true })
      },
    },
    showModalSettings: {
      get() {
        return this.isModalVisibleSettings
      },
      set(value) {
        this.$store.commit('layout/isModalVisibleSettings', value, { root: true })
      },
    },

    navState: {
      get() {
        return this.isNavCollapsed
      },
      set(value) {
        this.$store.commit('layout/isNavCollapsed', value)
      },
    },
    isDraggingNavigation: {
      get() {
        return this.isResizingNavigation
      },
      set(value) {
        this.$store.commit('layout/isResizingNavigation', value)
      },
    },

    drawerIcon() {
      return this.isNavCollapsed ? 'chevron_right' : 'chevron_left'
    },
    docRoute() {
      const { icons: { navigation: navIcons } } = this

      return {
        title: 'User Docs',
        image: navIcons.docs,
        link: '/docs',
        active: false,
        subItems: false,
      }
    },

    hasPermissionForSettings() {
      const { isActiveUser } = this

      return isActiveUser
    },
  },
  methods: {
    ...mapAuthActions(['signOut']),
    setNavState() {
      this.navState = !this.isNavCollapsed
    },
    displaySettingsModal() {
      this.showModalSettings = true
    },

    addCustomAnalytic() {
      const entry = {
        billingCode: null,
        reportPath: null,
        teamId: null,
      }

      this.formDataForCustomAnalytic = { action: 'create', entry }
      this.showModalCustomAnalytic = true
    },
    editCustomAnalyticsList() {
      this.entryCustomAnalytic = this.customAnalyticOptions
      this.showModalCustomAnalytic = true
    },
    editCustomAnalytic(customAnalytic) {
      this.entryCustomAnalytic = [customAnalytic]
      this.showModalCustomAnalytic = true
    },

    addTimeDateRange() {
      const entry = this.networks.map((dashboard) => {
        dashboard.isEditing = false
        return dashboard
      })

      entry.push({
        billingCode: null,
        index: entry.length,
        isEditing: true,
        label: null,
        team: null,
        teamId: null,
      })

      this.formDataForNetwork = { action: 'create', entry }
      this.showModalNetwork = true
    },
    editTimeDateRangeList() {
      const entry = this.networks.map((e) => {
        e.isEditing = true
        return e
      })

      this.formDataForNetwork = { action: 'update', entry }
      this.showModalNetwork = true
    },
    editTimeDateRange(networkIndex) {
      const entry = this.networks.map((e, eIndex) => {
        e.isEditing = eIndex === networkIndex
        return e
      })

      this.formDataForNetwork = { action: 'update', entry }
      this.showModalNetwork = true
    },

    navigateToCustomAnalytic(customAnalytic) {
      const { billingCode: associationId, href, reportPath, baseReportName, routeName, teamId: subId } = customAnalytic
      this.$store.dispatch('tools/updateRoute', { path: href, params: { reportPath, associationId, baseReportName, routeName, subId } })
    },
    navigateToTimeDateRange(dashboard) {
      const { billingCode: associationId, href, reportPath, baseReportName, routeName, teamId: subId } = dashboard
      this.$store.dispatch('tools/updateRoute', { path: href, params: { reportPath, associationId, baseReportName, routeName, subId } })
    },
    navigateToNetworkDashboard(dashboard) {
      const { billingCode: associationId, href, reportPath, baseReportName, routeName, teamId: subId } = dashboard
      this.$store.dispatch('tools/updateRoute', { path: href, params: { reportPath, associationId, baseReportName, routeName, subId } })
    },
    navigateToQuery(query) {
      const { reportPath, associationId, baseReportName, routeName, subId } = query

      this.$store.dispatch('tools/updateRoute', {
        params: { reportPath, baseReportName, routeName },
        query: { action: 'read', associationId, subId },
      })
    },

    resizeDrawerStart(event) {
      const elementDrawer = this.$refs.navDrawer.$el

      elementDrawer.style.transition = 'initial'

      this.newDrawerDragStartWidth = this.drawerWidth
      this.newDrawerDragStartX = event.pageX
      this.isDraggingNavigation = true

      const logger = console
      logger.info('Dragging has begun', this.drawerWidth, event.pageX)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

#drawer-resize {
  cursor: col-resize;
  height: 100%;
  padding: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 3px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.12);
  }
}

.is-resizing-navigation {
  background-color: rgba(0, 0, 0, 0.12);
}

.main-nav {
  display: flex;
  flex-direction: column;
}

.v-divider {
  border-color: $color-monochrome-icewhite;
}

.logo-container {
  display: flex;
  align-items: center;
  padding: 15px;

  button {
    margin: 0;
  }

  img {
    height: 21px;
  }
}

.logged-in-user-info {
  display: flex;
  align-items: center;

  ::v-deep.v-avatar {
    height: 39px !important;
    min-width: auto !important;
    width: 39px !important;

    transition-duration: 0.2s;
    transition-property: height, width, transform;

    box-shadow: 0 2px 6px 3px rgba(165, 177, 185, 0.3);
  }

  .user-details {
    padding: 15px 0 15px 15px;
    overflow: hidden;

    .user-name {
      letter-spacing: 0.43px;
      line-height: 1.2;
    }

    .user-email {
      letter-spacing: 0.36px;
      line-height: 1;
    }
  }
}

.menu-items {
  display: grid;

  .menu-item {
    color: $color-monochrome-darkestblue;

    img {
      height: 25px;
      width: 25px;
    }

    ::v-deep.v-list__tile {
      padding-top: 15px;
      padding-bottom: 15px;
      height: inherit;
    }
  }
}

.sub-items-container {
  .sub-item-link {
    padding-top: 9px;
    color: $color-monochrome-gray;
    font-size: $size-regular;
    letter-spacing: 0.36px;
    line-height: 18px;
    &:hover {
      color: #00aed6;
    }

    &.router-link-active {
      font-weight: bolder;
    }
  }
}

::v-deep.router-active-link {
  background-color: $color-offwhite;
  color: inherit;
}

.help-icon .v-icon {
  font-size: 33px;
  color: $color-monochrome-darkestblue;
}

.copywrite {
  color: $color-monochrome-darkestblue;
  font-size: $size-small;
  font-weight: 500;
  letter-spacing: 0.36px;
  line-height: 18px;
}

.footer-links {
  display: flex;
  justify-content: flex-start;
  padding-right: 48px;

  color: $color-monochrome-gray;
  font-size: $size-small;
  font-weight: 500;
  letter-spacing: 0.36px;
  line-height: 20px;

  a {
    color: inherit;
  }

  .bullet-divider {
    margin: 0 1ch;
  }
}

.v-navigation-drawer {
  box-shadow: 0 2px 6px 0 rgba(0, 148, 255, 0.21);
}

.v-navigation-drawer--mini-variant {
  box-shadow: none;

  .logo-container {
    justify-content: start;

    img {
      display: none;
    }
  }

  .logged-in-user-info {
    ::v-deep.v-avatar {
      height: 39px !important;
      width: 39px !important;
      transform: translateX(4px);
    }

    .user-details {
      display: none;
    }
  }

  .sub-items-container {
    display: none;
  }

  .nav-footer {
    .help-icon .v-icon {
      margin: 0;
      transform: translateX(8px);
    }

    .links-container {
      display: none;
    }
  }
}
</style>
